import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar, Plus, Clock, MapPin, Users, Loader2, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// Event types - keys for translation
const EVENT_TYPES = [
  { value: "appointment", labelKey: "calendar.appointment", color: "bg-blue-100 text-blue-700" },
  { value: "visit", labelKey: "calendar.visit", color: "bg-green-100 text-green-700" },
  { value: "medication", labelKey: "calendar.medication", color: "bg-purple-100 text-purple-700" },
  { value: "reminder", labelKey: "calendar.reminder", color: "bg-amber-100 text-amber-700" },
  { value: "milestone", labelKey: "calendar.milestone", color: "bg-pink-100 text-pink-700" },
];

export default function CareCalendar() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("month"); // month, week, list
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    event_type: "appointment",
    start_time: "",
    end_time: "",
    location: "",
    reminder_minutes: 60
  });

  useEffect(() => {
    if (user) {
      loadEvents();
    }
  }, [user, selectedDate]);

  const loadEvents = async () => {
    setIsLoading(true);
    try {
      const startOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      const endOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
      
      const response = await api.get("/calendar/events", {
        params: {
          start_date: startOfMonth.toISOString(),
          end_date: endOfMonth.toISOString()
        }
      });
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Failed to load events:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.start_time) {
      toast.error("Please fill in event title and start time");
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post("/calendar/events", newEvent);
      toast.success("Event added successfully");
      setShowAdd(false);
      setNewEvent({
        title: "",
        description: "",
        event_type: "appointment",
        start_time: "",
        end_time: "",
        location: "",
        reminder_minutes: 60
      });
      loadEvents();
    } catch (error) {
      toast.error("Failed to add event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await api.delete(`/calendar/events/${eventId}`);
      toast.success("Event deleted");
      loadEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.start_time?.startsWith(dateStr));
  };

  const getEventTypeStyle = (type) => {
    return EVENT_TYPES.find(t => t.value === type)?.color || "bg-gray-100 text-gray-700";
  };

  const formatTime = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (!user) {
    return (
      <div className="container-max px-6 py-12 text-center">
        <Calendar className="w-16 h-16 mx-auto mb-4 text-primary/30" />
        <h1 className="text-2xl font-serif mb-2">{t('calendar.title')}</h1>
        <p className="text-muted-foreground">{t('calendar.signInToView', 'Sign in to view your calendar')}</p>
      </div>
    );
  }

  return (
    <div className="container-max px-6 py-8" data-testid="care-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-serif font-medium">{t('calendar.title')}</h1>
          <p className="text-muted-foreground">{t('calendar.manageEvents', 'Manage appointments and events')}</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              {t('calendar.addEvent')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('calendar.addNewEvent', 'Add New Event')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('calendar.eventTitle', 'Event Title')}</Label>
                <Input
                  placeholder={t('calendar.eventTitlePlaceholder', 'e.g., Doctor Visit')}
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="event-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('calendar.eventType', 'Event Type')}</Label>
                <Select
                  value={newEvent.event_type}
                  onValueChange={(val) => setNewEvent(prev => ({ ...prev, event_type: val }))}
                >
                  <SelectTrigger data-testid="event-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map((eventType) => (
                      <SelectItem key={eventType.value} value={eventType.value}>{t(eventType.labelKey)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('calendar.startTime', 'Start Time')}</Label>
                  <Input
                    type="datetime-local"
                    value={newEvent.start_time}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, start_time: e.target.value }))}
                    data-testid="event-start-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('calendar.endTime', 'End Time')} ({t('common.optional', 'Optional')})</Label>
                  <Input
                    type="datetime-local"
                    value={newEvent.end_time}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, end_time: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('calendar.location', 'Location')} ({t('common.optional', 'Optional')})</Label>
                <Input
                  placeholder={t('calendar.locationPlaceholder', 'e.g., Goodwin Hospice Office')}
                  value={newEvent.location}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('calendar.description', 'Description')} ({t('common.optional', 'Optional')})</Label>
                <Textarea
                  placeholder={t('calendar.descriptionPlaceholder', 'Any additional details...')}
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('calendar.reminderLabel', 'Reminder')}</Label>
                <Select
                  value={newEvent.reminder_minutes.toString()}
                  onValueChange={(val) => setNewEvent(prev => ({ ...prev, reminder_minutes: parseInt(val) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">{t('calendar.15min', '15 minutes before')}</SelectItem>
                    <SelectItem value="30">{t('calendar.30min', '30 minutes before')}</SelectItem>
                    <SelectItem value="60">{t('calendar.1hour', '1 hour before')}</SelectItem>
                    <SelectItem value="1440">{t('calendar.1day', '1 day before')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                className="w-full" 
                onClick={handleAddEvent}
                disabled={isSubmitting}
                data-testid="save-event-btn"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                {t('calendar.addEvent')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>
                {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
              
              {/* Days */}
              {getDaysInMonth().map((date, i) => {
                const dayEvents = getEventsForDate(date);
                const isToday = date && date.toDateString() === new Date().toDateString();
                
                return (
                  <div 
                    key={i}
                    className={`min-h-[80px] p-1 border rounded-lg ${
                      date ? 'bg-card hover:bg-muted/50 cursor-pointer' : 'bg-muted/20'
                    } ${isToday ? 'border-primary border-2' : 'border-muted'}`}
                  >
                    {date && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${isToday ? 'text-primary' : ''}`}>
                          {date.getDate()}
                        </div>
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 2).map((event, j) => (
                            <div 
                              key={j}
                              className={`text-xs px-1 py-0.5 rounded truncate ${getEventTypeStyle(event.event_type)}`}
                            >
                              {event.title}
                            </div>
                          ))}
                          {dayEvents.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              +{dayEvents.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              {t('calendar.upcoming')}
            </CardTitle>
            <CardDescription>{t('calendar.next7days', 'Next 7 days')}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : events.length > 0 ? (
              <div className="space-y-3">
                {events
                  .filter(e => new Date(e.start_time) >= new Date())
                  .slice(0, 10)
                  .map((event) => (
                    <div 
                      key={event.id}
                      className="p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getEventTypeStyle(event.event_type)}>
                              {t(EVENT_TYPES.find(et => et.value === event.event_type)?.labelKey || 'calendar.appointment')}
                            </Badge>
                          </div>
                          <h4 className="font-medium text-sm">{event.title}</h4>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            <Clock className="w-3 h-3" />
                            {new Date(event.start_time).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            })} {t('calendar.at', 'at')} {formatTime(event.start_time)}
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          )}
                        </div>
                        <Button 
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDeleteEvent(event.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>{t('calendar.noUpcoming', 'No upcoming events')}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => setShowAdd(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t('calendar.addEvent')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
